// src/modules/TeamManager/index.tsx
import React, { useState, useEffect, useCallback } from 'react';
import TeamPermissionsPanel from './components/TeamPermissionsPanel';
import { Permission } from '../../utils/types';
import apiService from '../../services/api.service';
import Toast from '../../components/Toast';

// Complete Team interface matching API response
interface Team {
  season_id: string;
  team_entity_id: string;
  team_organization_id: string;
  team_external_id?: string | null;
  conference_id?: string | null;
  division_id?: string | null;
  roster_status?: string;
  season_team_status?: string;
  season_team_added?: string;
  season_team_updated?: string;
  team_name_latin: string;
  team_name_local: string;
  team_code_latin: string;
  team_code_local: string;
  team_representing?: string;
  logo_url?: string;
  final_standing?: number | null;
  final_points?: number | null;
  final_prize_money?: number | null;
}

export interface TeamPermission extends Permission {
  team?: Team;
}

interface TeamManagerProps {
  seasonId: string;
  seasonName: string;
  orgId: string;
}

// Cache to store loaded permissions by team ID
interface PermissionsCache {
  [teamId: string]: {
    permissions: TeamPermission[];
    loaded: boolean;
  };
}

const TeamManager: React.FC<TeamManagerProps> = ({ seasonId, seasonName, orgId }) => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [permissions, setPermissions] = useState<TeamPermission[]>([]);
  const [permissionsCache, setPermissionsCache] = useState<PermissionsCache>({});
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [loading, setLoading] = useState(false);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null);

  // Fetch teams
  const fetchTeams = useCallback(async () => {
    setTeamsLoading(true);
    try {
      const teamsData = await apiService.fetchTeamsForSeason(seasonId);
      setTeams(teamsData);
      
      // Initialize cache for all teams
      const cache: PermissionsCache = {};
      teamsData.forEach(team => {
        cache[team.team_entity_id] = {
          permissions: [],
          loaded: false
        };
      });
      setPermissionsCache(cache);
      
      // Select first team but don't load permissions yet
      if (teamsData.length > 0 && !selectedTeam) {
        setSelectedTeam(teamsData[0]);
      }
      
      return teamsData;
    } catch (error) {
      console.error('Error fetching teams:', error);
      setTeams([]);
      return [];
    } finally {
      setTeamsLoading(false);
    }
  }, [seasonId]);

  // Fetch permissions for a single team (lazy loading)
  const fetchTeamPermissions = useCallback(async (team: Team) => {
    // Check if already loaded
    if (permissionsCache[team.team_entity_id]?.loaded) {
      return;
    }

    setLoading(true);
    try {
      const teamPerms = await apiService.fetchTeamPermissions(team.team_entity_id);
      
      const teamPermissions: TeamPermission[] = teamPerms.map(perm => ({
        ...perm,
        team: team
      }));
      
      // Update cache
      setPermissionsCache(prev => ({
        ...prev,
        [team.team_entity_id]: {
          permissions: teamPermissions,
          loaded: true
        }
      }));
      
      // Update global permissions state
      setPermissions(prev => {
        // Remove old permissions for this team
        const filtered = prev.filter(p => p.teamId !== team.team_entity_id && p.team?.team_entity_id !== team.team_entity_id);
        // Add new permissions
        return [...filtered, ...teamPermissions];
      });
      
    } catch (error) {
      console.error('Error loading permissions for team:', team.team_entity_id, error);
      // Mark as loaded even on error to prevent repeated failed requests
      setPermissionsCache(prev => ({
        ...prev,
        [team.team_entity_id]: {
          permissions: [],
          loaded: true
        }
      }));
    } finally {
      setLoading(false);
    }
  }, [permissionsCache]);

  // Load teams on mount
  useEffect(() => {
    fetchTeams();
  }, [seasonId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load permissions when team is selected (lazy loading)
  useEffect(() => {
    if (selectedTeam && !permissionsCache[selectedTeam.team_entity_id]?.loaded) {
      fetchTeamPermissions(selectedTeam);
    }
  }, [selectedTeam]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddPermission = (team: Team) => {
    const newPermission: TeamPermission = {
      name: '',
      email: '',
      role: 'Team Manager',
      status: 'to-do',
      isNew: true,
      seasonId: seasonId,
      teamId: team.team_entity_id,
      team: team,
      orgId: orgId, // Use the passed orgId prop
      environment: 'production'
    };
    
    setPermissions([...permissions, newPermission]);
    
    // Update cache
    setPermissionsCache(prev => ({
      ...prev,
      [team.team_entity_id]: {
        permissions: [...(prev[team.team_entity_id]?.permissions || []), newPermission],
        loaded: true
      }
    }));
  };

  const handleSavePermissions = async (teamId: string) => {
    const teamPermissions = permissions.filter(p => p.teamId === teamId || p.team?.team_entity_id === teamId);
    
    if (teamPermissions.length === 0) {
      setToast({ message: 'No permissions to save', type: 'warning' });
      return;
    }

    setSaving(true);
    try {
      await apiService.saveTeamPermissions(teamPermissions);
      
      // Update status for saved permissions
      const updated = permissions.map(p => {
        if (p.teamId === teamId || p.team?.team_entity_id === teamId) {
          return {
            ...p,
            isNew: false,
            isEditing: false,
            status: p.status === 'to-do' ? 'active' : p.status
          };
        }
        return p;
      });
      
      setPermissions(updated);
      setToast({ message: '✨ Permissions saved successfully!', type: 'success' });
      
      // Reload permissions for this team only to get IDs
      const team = teams.find(t => t.team_entity_id === teamId);
      if (team) {
        // Mark as not loaded to force refresh
        setPermissionsCache(prev => ({
          ...prev,
          [teamId]: {
            permissions: [],
            loaded: false
          }
        }));
        // Reload permissions for this team
        await fetchTeamPermissions(team);
      }
    } catch (error) {
      console.error('Error saving permissions:', error);
      setToast({ message: 'Failed to save permissions', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const getTeamPermissions = (teamId: string) => {
    return permissions.filter(p => p.teamId === teamId || p.team?.team_entity_id === teamId);
  };

  const handleTeamSelect = (team: Team) => {
    setSelectedTeam(team);
    // Permissions will be loaded automatically via useEffect
  };

  if (teamsLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
      </div>
    );
  }

  return (
    <div>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="mb-6">
        <h2 className="text-lg font-medium text-gray-900">
          Team Permissions for {seasonName}
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Select a team from the list to manage its permissions
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Team List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                Teams ({teams.length})
              </h3>
            </div>
            <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
              {teams.map((team) => {
                const teamPerms = getTeamPermissions(team.team_entity_id);
                const isSelected = selectedTeam?.team_entity_id === team.team_entity_id;
                const isLoaded = permissionsCache[team.team_entity_id]?.loaded;
                
                return (
                  <button
                    key={team.team_entity_id}
                    onClick={() => handleTeamSelect(team)}
                    className={`w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors ${
                      isSelected ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      {team.logo_url && (
                        <img 
                          src={team.logo_url} 
                          alt={team.team_name_latin}
                          className="h-8 w-8 object-contain rounded"
                        />
                      )}
                      <div className="text-left">
                        <div className="text-sm font-medium text-gray-900">
                          {team.team_name_latin || team.team_name_local}
                        </div>
                        <div className="text-xs text-gray-500">
                          {team.team_code_latin}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {/* Show loading spinner for this team if selected and loading */}
                      {isSelected && loading && !isLoaded && (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      )}
                      {/* Show permission count if loaded */}
                      {isLoaded && teamPerms.length > 0 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {teamPerms.length}
                        </span>
                      )}
                      {/* Show dot indicator if not loaded yet */}
                      {!isLoaded && !isSelected && (
                        <span className="inline-flex h-2 w-2 rounded-full bg-gray-300"></span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Permissions Panel */}
        <div className="lg:col-span-2">
          {selectedTeam ? (
            <TeamPermissionsPanel
              team={selectedTeam}
              permissions={getTeamPermissions(selectedTeam.team_entity_id)}
              setPermissions={setPermissions}
              allPermissions={permissions}
              onAddPermission={() => handleAddPermission(selectedTeam)}
              onSave={() => handleSavePermissions(selectedTeam.team_entity_id)}
              saving={saving}
              loading={loading && !permissionsCache[selectedTeam.team_entity_id]?.loaded}
            />
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-16 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <p className="text-gray-500">Select a team to manage permissions</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamManager;